import { Typography, Space } from 'antd'
import { useEffect, useState } from 'react'
import LoadableImage from 'components/LoadableImage'
import { LayerPreviewProps, LayerPreviewType } from './types'
import CIMSSelect from 'components/CIMSSelect'
import { alphabeticalSort, an, notEqual } from '@cims-online/shared'
import { PDFDesignLayerType } from '../../../../types'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useMapImageLazyQuery } from './hooks/useMapImageLazyQuery'
import { usePreviewOptions } from './hooks/usePreviewOptions'
import { usePDFDesignLayersContext } from '../../../../PDFDesignLayersContext'
import { CIMSTags } from 'components/CIMSTags'
import CIMSBadge from 'components/CIMSBadge'
import CIMSButton from 'components/CIMSButton'

const LayerPreview: React.FC<LayerPreviewProps> = ({
  typename,
  projectLayerIds,
  globalId: typenameGlobalId,
}) => {
  const { projectLayers } = usePDFDesignLayersContext()
  const [globalId, setGlobalId] = useState<string | undefined>()
  const [previewTypename, setPreviewTypename] = useState<
    keyof typeof LayerPreviewType
  >(PDFDesignLayerType[typename].previewTypes[0])
  const mapImageLazyQuery = useMapImageLazyQuery(previewTypename)
  const previewOptions = usePreviewOptions(
    typename,
    typenameGlobalId || undefined
  )

  useEffect(() => {
    setPreviewTypename(PDFDesignLayerType[typename].previewTypes[0])
  }, [typename])
  useEffect(() => {
    setGlobalId(undefined)
  }, [previewTypename, typenameGlobalId])

  const options = useOptions({
    items: [...PDFDesignLayerType[typename].previewTypes],
    type: 'custom',
    sort: alphabeticalSort(),
    custom: (item) => ({
      label: (
        <CIMSBadge count={previewOptions[item].options.length}>
          {LayerPreviewType[item].name}
        </CIMSBadge>
      ),
      value: item,
    }),
  })

  return (
    <Space direction="vertical" size="middle" style={{ maxWidth: 500 }}>
      <Typography.Text>
        Select {an(LayerPreviewType[previewTypename].name)} to generate a
        preview with the configured project feature layer(s).
      </Typography.Text>
      <Space>
        <CIMSSelect
          options={options}
          value={previewTypename}
          loading={
            previewOptions[previewTypename].loading || mapImageLazyQuery.loading
          }
          simple
          allowClear={false}
          onChange={(value) =>
            setPreviewTypename(value as keyof typeof LayerPreviewType)
          }
          style={{ minWidth: 150 }}
        />
        <CIMSSelect
          options={previewOptions[previewTypename].options}
          value={globalId}
          simple
          onChange={(value) => setGlobalId(value as string)}
          disabled={!previewTypename || mapImageLazyQuery.loading}
          loading={
            mapImageLazyQuery.loading || previewOptions[previewTypename].loading
          }
          style={{ minWidth: 150 }}
        />
        <CIMSButton
          type="primary"
          loading={mapImageLazyQuery.loading}
          disabled={[
            {
              disabled: typenameGlobalId === false ? false : !typenameGlobalId,
              reason: {
                why: 'disabled',
                message: `You must select a ${PDFDesignLayerType[typename].name}`,
              },
            },
            {
              disabled: !globalId,
              reason: {
                why: 'disabled',
                message: `You must select a ${LayerPreviewType[previewTypename].name}`,
              },
            },
            {
              disabled: mapImageLazyQuery.loading,
              reason: 'loading',
            },
            {
              disabled: previewOptions[previewTypename].loading,
              reason: 'loading',
            },
          ]}
          onClick={() => {
            globalId && mapImageLazyQuery.getMapImage(globalId, projectLayerIds)
          }}
        >
          Generate Preview
        </CIMSButton>
      </Space>
      <CIMSTags
        tags={(
          projectLayers.data?.project.projectMetadata?.mapServerLayers ?? []
        )
          .filter(({ id }) => projectLayerIds?.includes(id))
          .map(({ name }) => ({ text: name }))}
      />
      {mapImageLazyQuery.src &&
        (notEqual(projectLayerIds, mapImageLazyQuery.lastProjectLayerIds) ||
          notEqual(globalId, mapImageLazyQuery.lastGlobalId)) && (
          <Typography.Text type="warning">
            *The current preview may not reflect the final image.
          </Typography.Text>
        )}
      <LoadableImage
        height={300}
        width={450}
        alt={`${previewTypename} ${globalId}`}
        loading={mapImageLazyQuery.loading}
        src={mapImageLazyQuery.src}
        preview={{ height: 700, width: 1050 }}
      />
    </Space>
  )
}

export default LayerPreview
